// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo, type ReactNode } from 'react';

import type { BendType } from '@evolvemep/api-ts';
import { Card, Flex, Loader, type MantineColor, Text, ThemeIcon } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { hasLengthExactly } from 'helpers/helper-types';
import { isAlmostExactlyEqual } from 'helpers/isAlmostExactlyEqual';
import { isNil } from 'helpers/isNotNil';
import { stringListToNaturalLanguage } from 'helpers/stringFunctions';
import { useBendCreatorFormContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import { useBendCreatorValidationContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorValidationContext';
import { getCenterBendKeys } from 'modules/Shop/EvolveFabrication/BendCreator/State/centerBend';

type StatusCardProps = {
  bg: MantineColor;
  c: MantineColor;
  icon: ReactNode;
  text: ReactNode;
};

const ValidationStatusCard = ({ bg, c, icon, text }: StatusCardProps) => (
  <Card bg={bg} c={c} withBorder>
    <Flex gap="sm" align="center">
      <ThemeIcon radius="xl" color={c}>
        {icon}
      </ThemeIcon>
      <Text fz="sm">{text}</Text>
    </Flex>
  </Card>
);

type Props = {
  currentBendTypeData: BendType | null;
};

export const BendValidationStatus = ({ currentBendTypeData }: Props) => {
  const { isRequestInFlight, lastValidation } = useBendCreatorValidationContext();
  const {
    values: { centerBend },
  } = useBendCreatorFormContext();

  const statusCardProps = useMemo<StatusCardProps | null>(() => {
    // Only while a request is in flight: during the short wait before one is sent, the card would flicker as the
    // user steps a value.
    if (isRequestInFlight) {
      return {
        bg: 'gray.0',
        c: 'gray.6',
        icon: <Loader size={24} color="white" />,
        text: 'Validating bend dimensions',
      };
    }
    if (isNil(lastValidation)) return null;
    if (!lastValidation.isSuccess) {
      return {
        bg: 'red.1',
        c: 'red.8',
        icon: <EvolveIcon icon="Alert" size="md" />,
        text: 'Bend dimensions are invalid',
      };
    }
    const { request, response } = lastValidation;
    // If the last request was for a different bend type than the current one, ignore it.
    if (lastValidation.request.bendType !== currentBendTypeData?.name) return null;

    // Center Bend sets its own fields, so the server changing them is not an adjustment to report.
    const centeredKeys = centerBend ? getCenterBendKeys(request.bendType) : [];
    const alteredKeys = response.updatedBend.filter(
      ({ keyName, value }) =>
        !centeredKeys.includes(keyName) && (isNil(request[keyName]) || !isAlmostExactlyEqual(request[keyName], value)),
    );
    if (hasLengthExactly(alteredKeys, 0)) {
      return {
        bg: 'green.1',
        c: 'teal.8',
        icon: <EvolveIcon icon="Check" size="md" />,
        text: 'Bend dimensions validated',
      };
    }
    const labelMap = new Map(currentBendTypeData.inputs.map(({ keyName, name }) => [keyName, name]));
    const alteredKeyLabels = alteredKeys.map(({ keyName }) => labelMap.get(keyName) ?? keyName);
    return {
      bg: 'orange.1',
      c: 'orange.8',
      icon: <EvolveIcon icon="CheckAlert" size="md" />,
      text: (
        <>
          Adjusted <b>{stringListToNaturalLanguage(alteredKeyLabels)}</b> to pass bend validation
        </>
      ),
    };
  }, [isRequestInFlight, lastValidation, currentBendTypeData, centerBend]);

  if (isNil(statusCardProps)) return null;
  return <ValidationStatusCard {...statusCardProps} />;
};
